import React, { useRef, useState } from 'react';
import { Button, ButtonGroup, CircularProgress } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useDispatch } from 'react-redux';
import { logError } from '@actions/userActions';
import { printFoodOrderBill } from '@components/Print';
import { printFoodOrderKOT } from '@components/Print/PrintKOTPrint';
import { resolveKitchenPrintChannel } from '@components/Print/PrintUtility';
import { ConfirmationModal } from '@components/ConfirmModal/Confirmation';
import type { OnlineOrder } from '@src/types/onlineOrder';
import {
  onlineOrderToPrintBill,
  onlineOrderToPrintKot,
} from './onlineOrderPrintAdapter';

interface OnlineOrderPrintActionsProps {
  order: OnlineOrder;
  restaurant: Record<string, unknown> | null | undefined;
  canPrintKOT: boolean;
  onClickCapture?: (event: React.MouseEvent) => void;
  compact?: boolean;
  className?: string;
}

function handlePrintError(dispatch: ReturnType<typeof useDispatch>, error: unknown) {
  const message =
    (error as Error)?.message === 'Nothing to print'
      ? 'Nothing to Print :)'
      : (error as Error)?.message || 'Failed to print';
  dispatch(
    logError({
      severity: (error as Error)?.message === 'Nothing to print' ? 'info' : 'error',
      message,
    })
  );
}

export default function OnlineOrderPrintActions({
  order,
  restaurant,
  canPrintKOT,
  onClickCapture,
  compact = false,
  className = '',
}: OnlineOrderPrintActionsProps) {
  const dispatch = useDispatch();
  const [printingKot, setPrintingKot] = useState(false);
  const [printingBill, setPrintingBill] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingKotRef = useRef(false);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClickCapture?.(event);
  };

  const runKotPrint = async () => {
    setPrintingKot(true);
    try {
      await printFoodOrderKOT(onlineOrderToPrintKot(order));
    } catch (error) {
      handlePrintError(dispatch, error);
      throw error;
    } finally {
      setPrintingKot(false);
    }
  };

  const handlePrintKot = async (event: React.MouseEvent<HTMLButtonElement>) => {
    stopPropagation(event);
    try {
      const channel = await resolveKitchenPrintChannel();
      if (channel === 'browser') {
        pendingKotRef.current = true;
        setConfirmOpen(true);
        return;
      }
      await runKotPrint();
    } catch (error) {
      handlePrintError(dispatch, error);
    }
  };

  const handleConfirmKot = async () => {
    if (!pendingKotRef.current) {
      setConfirmOpen(false);
      return;
    }
    try {
      await runKotPrint();
      pendingKotRef.current = false;
      setConfirmOpen(false);
    } catch {
      // keep modal open for retry/cancel
    }
  };

  const handleCancelKot = () => {
    if (printingKot) return;
    pendingKotRef.current = false;
    setConfirmOpen(false);
  };

  const handlePrintInvoice = async (event: React.MouseEvent<HTMLButtonElement>) => {
    stopPropagation(event);
    if (!restaurant) {
      dispatch(logError({ severity: 'error', message: 'Restaurant details not loaded' }));
      return;
    }
    setPrintingBill(true);
    try {
      await printFoodOrderBill(onlineOrderToPrintBill(order, restaurant), restaurant);
    } catch (error) {
      handlePrintError(dispatch, error);
    } finally {
      setPrintingBill(false);
    }
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        color="secondary"
        size="small"
        className={`online-order-print-actions${compact ? ' online-order-print-actions--compact' : ''}${className ? ` ${className}` : ''}`}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {canPrintKOT && (
          <Button
            onClick={handlePrintKot}
            disabled={printingKot || printingBill}
            title="Print KOT"
            className="online-order-print-actions__kot"
          >
            {printingKot ? <CircularProgress size={14} color="inherit" /> : 'KOT'}
          </Button>
        )}
        <Button
          onClick={handlePrintInvoice}
          disabled={printingKot || printingBill}
          title="Print Invoice"
          className="online-order-print-actions__invoice"
        >
          {printingBill ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <>
              <PrintIcon fontSize="small" />
              {!compact && ' Invoice'}
            </>
          )}
        </Button>
      </ButtonGroup>
      <ConfirmationModal
        open={confirmOpen}
        title="Print KOT?"
        message="This will send the kitchen order ticket to the printer. Cancel if you opened this by mistake — nothing will be printed or logged."
        confirmLabel={printingKot ? 'Printing…' : 'Print KOT'}
        cancelLabel="Cancel"
        severity="info"
        onConfirm={handleConfirmKot}
        onCancel={handleCancelKot}
      />
    </>
  );
}
